import { defineStore } from "pinia";
import { useStorage } from "@vueuse/core";

import { findIndex, isNull, keyBy, keys } from "lodash";

import Applications from "@/helpers/clients/Applications";
import { Application, ApplicationStep } from "@/types";
import { AxiosResponse } from "axios";

export interface ApplicationState {
  application: Application | null;
  cachedQuotes: any;
  open: boolean;
  serverValidationErrors: any;
}

const applicationsClient: Applications = new Applications();

export const useApplicationStore = defineStore("application", {
  state: (): ApplicationState => {
    return {
      application: null,
      cachedQuotes: useStorage("cachedApplications", []) as any,
      open: false,
      serverValidationErrors: null,
    };
  },

  actions: {
    setApplicationSteps(steps: ApplicationStep[]): void {
      if (!this.application) {
        return;
      }

      this.application.steps = steps;
    },

    cleanCachedQuotes() {
      this.cachedQuotes = [];
    },

    getCachedQuotes() {
      return this.cachedQuotes.map((applicationId: string) => {
        return { id: applicationId };
      });
    },

    getComponentErrorByName(name: string = "", parent: string, index: number) {
      if (!this.serverValidationErrors.errors?.data || !name) {
        return null;
      }

      return this.findComponentErrors(this.serverValidationErrors.errors?.data, name, parent, index);
    },

    filterApplicationStepsByStatus(step_statuses: string[]) {
      if (!isNull(this.application)) {
        return this.application.steps?.filter((step: any) =>
          !step_statuses.length ||
          step.grouped_tags?.quote_step_status?.some(({ id }: any) =>
            step_statuses.includes(id)
          )
        );
      }

      return [];
    },

    findComponentErrors(errors: any, name: string, parent: string, index: number): any {
      if (errors[name]) {
        return errors[name]["_schema"] ? errors[name]["_schema"] : errors[name];
      } else if (parent && typeof errors !== "string") {
        if (errors[parent]) {
          return this.findErrorsByParent(errors, name, parent, index);
        } else {
          return this.findErrorsInSubLevel(errors, name, parent, index);
        }
      }

      return null;
    },

    findErrorsByParent(errors: any, name: string, parent: string, index: number): any {
      let foundErrors: any = null;
      if (index >= 0) {
        if (errors[parent][index]) {
          if (errors[parent][index]["_schema"] && !name) {
            foundErrors = errors[parent][index]["_schema"];
          } else {
            foundErrors = this.findComponentErrors(errors[parent][index], name, parent, index);
          }
        }
      } else {
        foundErrors = this.findComponentErrors(errors[parent], name, parent, index);
      }

      return foundErrors;
    },

    findErrorsInSubLevel(errors: any, name: string, parent: string, index: number): any {
      let foundErrors: any = null;

      for (const componentName of keys(errors)) {
        if (!Array.isArray(errors[componentName])) {
          if (
            errors[componentName][index] && // NOSONAR
            errors[componentName][index][parent] &&
            errors[componentName][index][parent][name]
          ) {
            foundErrors = this.findComponentErrors(errors[componentName][index][parent], name, parent, index);
          } else {
            foundErrors = this.findComponentErrors(errors[componentName], name, parent, index);
          }

          if (foundErrors) {
            break;
          }
        }
      }

      return foundErrors;
    },

    async loadApplication(id: string): Promise<void> {
      this.application = null;

      const response: any = await applicationsClient.read(id);

      this.application = response.data;
    },

    async loadApplicationSteps({ id, params, force = false }: { id: string, params: Record<string, any>, force?: boolean }): Promise<void> {
      if (this.application?.id === id && this.application.steps && this.application.steps?.length > 0 && !force) {
        return;
      }

      const response: AxiosResponse = await applicationsClient.searchSteps(id, params);

      this.setApplicationSteps(response.data.results);
    },

    async getStepData({ applicationId, stepId }: { applicationId: string, stepId: string }): Promise<ApplicationStep> {
      const response: AxiosResponse<ApplicationStep> = await applicationsClient.readStepData(applicationId, stepId);

      return response.data;
    },

    replaceApplicationStep(step: ApplicationStep): void {
      if (this.application?.steps) {
        const index: number = findIndex(this.application.steps, { id: step.id });

        if (index >= 0) {
          this.application.steps[index] = step;
        }
      }
    },

    toggleOpen() {
      this.open = !this.open;
    },

    setCachedQuotes(applicationId: string) {
      this.cachedQuotes.push(applicationId);
    },
  },
});
